import { omit } from "@msys/common";
import { useTranslate } from "@tolgee/react";
import { isNil } from "lodash-es";
import React from "react";
import {
  FilterChipBoolean,
  FilterChipGroup,
} from "../../commons/filters/FilterChip.js";
import { OutgoingInvoicesFilter } from "../../main-routes/documents/Invoices.js";
import { CrmCompanyFilterChip } from "../crm-companies/CrmCompanyFilterChip.js";
import { useInvoiceStatus } from "./useInvoiceStatus.js";

export function useInvoicesActiveFilterChips<F extends OutgoingInvoicesFilter>({
  filters,
  setFilters,
  tab,
}: {
  filters: F;
  setFilters?: React.Dispatch<React.SetStateAction<F>>;
  tab: "outgoing" | "incoming";
}): Array<JSX.Element> {
  const chips: Array<JSX.Element> = [];
  const { t } = useTranslate(["ProjectOverview", "Invoices", "Global"]);
  const { outgoingStatusLabels } = useInvoiceStatus();

  if (filters.filterByCrmOrganisationId) {
    chips.push(
      <CrmCompanyFilterChip
        key="crm-organisation"
        value={filters?.filterByCrmOrganisationId}
        onDelete={
          setFilters
            ? () =>
                setFilters(filterVariables => ({
                  ...filterVariables,
                  filterByCrmOrganisationId: null,
                }))
            : undefined
        }
        label={
          tab === "outgoing"
            ? t("Client name", { ns: "Invoices" })
            : t("Contractor name", { ns: "Invoices" })
        }
      />
    );
  }

  if (filters.filterStatus) {
    chips.push(
      <FilterChipGroup
        key="invoice-status"
        label={t("Status", {
          ns: "Global",
        })}
        values={filters.filterStatus}
        setValues={
          setFilters
            ? value =>
                value && value.length > 0
                  ? setFilters(filters => ({
                      ...filters,
                      filterStatus: value,
                    }))
                  : setFilters(filters => omit(filters, "filterStatus") as F)
            : undefined
        }
        getValueLabel={value => outgoingStatusLabels[value]}
      />
    );
  }

  if (!isNil(filters.filterOverdue)) {
    chips.push(
      <FilterChipBoolean
        key="overdue"
        label={t("Overdue", {
          ns: "ProjectOverview",
        })}
        value={filters.filterOverdue}
        onDelete={
          setFilters
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  filterOverdue: undefined, // FIXME: remove instead
                }))
            : undefined
        }
      />
    );
  }

  return chips;
}
